import { useEffect, useState } from 'react';
import { BrowserRouter, Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { api } from './api';
import { MeCtx, ToastProvider, type Me, Loading } from './components/ui';
import Icon, { type IconName } from './components/Icon';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Campaigns from './pages/Campaigns';
import Clients from './pages/Clients';
import ClientPage from './pages/ClientPage';
import CampaignPage from './pages/CampaignPage';
import MediaLibrary from './pages/MediaLibrary';
import SpecReview from './pages/SpecReview';
import SetWizard from './pages/SetWizard';
import Editor from './pages/Editor';
import ExportCenter from './pages/ExportCenter';
import Settings from './pages/Settings';
import BrandImport from './pages/BrandImport';
import TrackingLinks from './pages/TrackingLinks';
import DemoSite from './pages/DemoSite';
import Jobs from './pages/Jobs';
import SetPassword from './pages/SetPassword';
import QuickCampaign from './pages/QuickCampaign';
import ImportDesign from './pages/ImportDesign';
import AdToBanners from './pages/AdToBanners';

const PRIMARY: { to: string; label: string; icon: IconName; end?: boolean }[] = [
  { to: '/', label: 'בית', icon: 'home', end: true },
  { to: '/campaigns', label: 'קמפיינים', icon: 'megaphone' },
  { to: '/clients', label: 'לקוחות', icon: 'users' },
  { to: '/media', label: 'אתרי מדיה', icon: 'globe' },
  { to: '/settings', label: 'הגדרות', icon: 'settings' },
];
const TOOLS: { to: string; label: string; icon: IconName }[] = [
  { to: '/exports', label: 'בדיקות ויצוא', icon: 'download' },
  { to: '/demo', label: 'אתר דמו', icon: 'play' },
  { to: '/jobs', label: 'עבודות ברקע', icon: 'activity' },
];

function AiNotice({ provider }: { provider: string | null }) {
  if (provider !== 'mock') return null;
  return (
    <div className="mock-banner" role="note">
      <Icon name="alert" />
      <span>ה-AI עדיין לא מחובר, ולכן העיצובים הם תבניות בסיסיות. <Link to="/settings">הגדרת מפתח AI</Link></span>
    </div>
  );
}

function Shell({ me, ai }: { me: Me; ai: { provider: string } | null }) {
  const nav = useNavigate();
  const loc = useLocation();
  const logout = async () => { await api.post('/api/auth/logout'); nav('/login'); location.reload(); };
  // בעורך ובאשף כל המסך לעבודה — מעבירים פוקוס לתוכן בכל מעבר דף (נגישות)
  useEffect(() => { document.getElementById('main')?.focus({ preventScroll: true }); window.scrollTo(0, 0); }, [loc.pathname]);
  const initials = (me.user.name || me.user.email).trim().slice(0, 1).toUpperCase();
  return (
    <div className="app">
      <a className="skip-link" href="#main">דילוג לתוכן</a>
      <header className="mobile-bar">
        <span className="row" style={{ gap: 10 }}><span className="brand-mark"><Icon name="layers" size={20} /></span>בונה באנרים</span>
        <button className="btn ghost small" onClick={logout} aria-label="יציאה"><Icon name="logout" size={18} /></button>
      </header>
      <nav className="sidebar" aria-label="ניווט ראשי">
        <div className="brand"><span className="brand-mark"><Icon name="layers" size={20} /></span>בונה באנרים</div>
        <NavLink to="/new" className="nav-cta"><Icon name="plus" size={20} />קמפיין חדש</NavLink>
        {PRIMARY.map((n) => <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}><Icon name={n.icon} />{n.label}</NavLink>)}
        <div className="nav-sep" />
        <div className="nav-label">כלים</div>
        {TOOLS.map((n) => <NavLink key={n.to} to={n.to} className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}><Icon name={n.icon} />{n.label}</NavLink>)}
        <div className="grow-space" />
        <div className="userbox">
          <span className="avatar" aria-hidden="true">{initials}</span>
          <span className="who"><strong>{me.user.name}</strong><span>{me.workspace.name}</span></span>
          <button className="btn ghost small" onClick={logout} aria-label="יציאה מהמערכת" title="יציאה"><Icon name="logout" size={18} /></button>
        </div>
      </nav>
      <main className="main" id="main" tabIndex={-1} style={{ outline: 'none' }}>
        <AiNotice provider={ai?.provider ?? null} />
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/new" element={<QuickCampaign />} />
          <Route path="/import" element={<ImportDesign />} />
          <Route path="/ad" element={<AdToBanners />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/clients/:id" element={<ClientPage />} />
          <Route path="/campaigns/:id" element={<CampaignPage />} />
          <Route path="/campaigns/:id/links" element={<TrackingLinks />} />
          <Route path="/media" element={<MediaLibrary />} />
          <Route path="/spec-versions/:id" element={<SpecReview />} />
          <Route path="/sets/new" element={<SetWizard />} />
          <Route path="/sets/:id" element={<SetWizard />} />
          <Route path="/variants/:id/edit" element={<Editor />} />
          <Route path="/exports" element={<ExportCenter />} />
          <Route path="/brand-import" element={<BrandImport />} />
          <Route path="/demo" element={<DemoSite />} />
          <Route path="/jobs" element={<Jobs />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
      <nav className="bottom-nav" aria-label="ניווט תחתון">
        <NavLink to="/" end className={({ isActive }) => (isActive ? 'active' : '')}><Icon name="home" />בית</NavLink>
        <NavLink to="/campaigns" className={({ isActive }) => (isActive ? 'active' : '')}><Icon name="megaphone" />קמפיינים</NavLink>
        <NavLink to="/new" className={({ isActive }) => `fab${isActive ? ' active' : ''}`}><Icon name="plus" size={26} />חדש</NavLink>
        <NavLink to="/clients" className={({ isActive }) => (isActive ? 'active' : '')}><Icon name="users" />לקוחות</NavLink>
        <NavLink to="/settings" className={({ isActive }) => (isActive ? 'active' : '')}><Icon name="settings" />הגדרות</NavLink>
      </nav>
    </div>
  );
}

export default function App() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [ai, setAi] = useState<{ provider: string } | null>(null);
  useEffect(() => {
    api.get<Me>('/api/me').then(setMe).catch(() => setMe(null));
    const refresh = () => api.get('/api/health').then((h) => setAi({ provider: h.aiProvider })).catch(() => {});
    refresh();
    // מתעדכן מיד אחרי שינוי הגדרות AI, וגם כשחוזרים לחלון
    window.addEventListener('ai-config-changed', refresh);
    window.addEventListener('focus', refresh);
    return () => { window.removeEventListener('ai-config-changed', refresh); window.removeEventListener('focus', refresh); };
  }, []);
  if (me === undefined) return <Loading />;
  return (
    <ToastProvider>
      <BrowserRouter>
        {me === null ? (
          <Routes><Route path="/set-password" element={<SetPassword />} /><Route path="*" element={<Login onLogin={() => api.get<Me>('/api/me').then(setMe)} />} /></Routes>
        ) : (
          <MeCtx.Provider value={me}><Shell me={me} ai={ai} /></MeCtx.Provider>
        )}
      </BrowserRouter>
    </ToastProvider>
  );
}
